import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import { Mountain, CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const { token } = useAuth();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setPaymentInfo(res.data);

        if (res.data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setStatus("error");
      }
    };

    if (sessionId && token) {
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus("error");
    }
  }, [sessionId, token]);

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Mountain className="h-10 w-10 text-orange-600" />
          <span className="font-serif text-2xl font-medium text-slate-900">Steve Hanson</span>
        </div>

        <Card>
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="h-16 w-16 text-orange-600 mx-auto mb-6 animate-spin" />
                <h2 className="text-2xl font-serif font-medium text-slate-900 mb-2">
                  Processing Payment...
                </h2>
                <p className="text-slate-600">
                  Please wait while we confirm your payment.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-serif font-medium text-slate-900 mb-2">
                  Payment Successful!
                </h2>
                <p className="text-slate-600 mb-6">
                  Thank you for your purchase. Your account has been upgraded to premium 
                  and you now have full access to all content and features.
                </p>
                {paymentInfo && (
                  <div className="bg-stone-50 rounded-lg p-4 mb-6 text-left">
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Amount:</span> ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </p>
                  </div>
                )}
                <div className="space-y-3">
                  <Link to="/content" className="block">
                    <Button className="w-full bg-orange-600 hover:bg-orange-500">
                      Access Premium Content
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/booking" className="block">
                    <Button variant="outline" className="w-full">
                      Schedule Your First Session
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {(status === "error" || status === "expired" || status === "timeout") && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="h-10 w-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-serif font-medium text-slate-900 mb-2">
                  {status === "expired" ? "Session Expired" : "Something Went Wrong"}
                </h2>
                <p className="text-slate-600 mb-6">
                  {status === "expired" 
                    ? "Your payment session has expired. Please try again."
                    : "We couldn't verify your payment. If you were charged, please contact us."}
                </p>
                <div className="space-y-3">
                  <Link to="/#services" className="block">
                    <Button className="w-full bg-orange-600 hover:bg-orange-500">
                      Try Again
                    </Button>
                  </Link>
                  <Link to="/#contact" className="block">
                    <Button variant="outline" className="w-full">
                      Contact Support
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          <Link to="/" className="hover:text-slate-700">
            Return to Home
          </Link>
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
